import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/contexts/ThemeContext";
import { useEffect, useMemo } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { isNative, getPlatform } from "@/utils/platform";
import { useChromeOverride } from "@/contexts/ChromeContext";

export const Layout = ({
  children,
  hideSidebar = false,
  hideNavbar = false,
  hideNavigation = false,
  hideFooter = false
}: {
  children: React.ReactNode,
  hideSidebar?: boolean,
  hideNavbar?: boolean,
  hideNavigation?: boolean,
  hideFooter?: boolean
}) => {
  void hideNavigation;
  const { user } = useAuth();
  const { translate } = useLanguage();
  const { theme } = useTheme();

  const chromeOverride = useMemo(
    () => ({ hideNavbar, hideSidebar, hideFooter }),
    [hideNavbar, hideSidebar, hideFooter]
  );
  useChromeOverride(chromeOverride);

  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    if (meta) {
      meta.setAttribute(
        'content',
        'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0, viewport-fit=cover'
      );
    }

    if (getPlatform() === 'android') {
      let referrerMeta = document.querySelector('meta[name="referrer"]');
      if (!referrerMeta) {
        referrerMeta = document.createElement('meta');
        referrerMeta.setAttribute('name', 'referrer');
        document.head.appendChild(referrerMeta);
      }
      referrerMeta.setAttribute('content', 'no-referrer');
    }

    let colorMeta = document.querySelector('meta[name="theme-color"]');
    if (!colorMeta) {
      colorMeta = document.createElement('meta');
      colorMeta.setAttribute('name', 'theme-color');
      document.head.appendChild(colorMeta);
    }
    colorMeta.setAttribute('content', theme === 'dark' ? '#0f172a' : '#ffffff');
    return () => {
      if (meta) {
        meta.setAttribute('content',
          'width=device-width, initial-scale=1.0, viewport-fit=cover'
        );
      }
      if (colorMeta) {
        colorMeta.remove();
      }
    };
  }, [theme]);

  const showBottomNav = isNative() && !!user && !hideNavbar;
  const rootClass = hideFooter
    ? getPlatform() === 'android'
      ? 'h-[calc(100vh_-_var(--kb-h,0px))] overflow-hidden'
      : 'h-screen overflow-hidden'
    : 'min-h-screen';

  return (
    <div className={`relative bg-[#F1F0FB]/20 dark:bg-gray-900 ${rootClass}`}>
      <main className={`
        ${!hideNavbar ? 'pt-[calc(4rem+env(safe-area-inset-top))]' : ''}
        ${!hideSidebar && user ? 'lg:pl-64' : ''}
        ${hideFooter ? 'h-full' : 'min-h-screen'}
        ${showBottomNav ? 'pb-[calc(5rem+env(safe-area-inset-bottom))]' : (hideFooter ? '' : 'pb-24')}
      `}>
        {children}
      </main>
      {!hideFooter && !showBottomNav && (
        <footer className="text-center py-4 text-sm text-[#8E9196] dark:text-gray-400 mt-auto">
          <div className="container mx-auto">
            {translate("footer.copyright").replace("{year}", new Date().getFullYear().toString())}
          </div>
        </footer>
      )}
    </div>
  );
};
